import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "react-hot-toast";

import { PollResults } from "@/components/poll-results";
import { apiErrorMessage } from "@/lib/api";
import {
  livePollQuery,
  rememberVote,
  rememberedVote,
  voteInPoll,
} from "@/lib/polls";

export function LivePoll() {
  const queryClient = useQueryClient();
  const { data: poll } = useQuery(livePollQuery());
  const [voting, setVoting] = useState<string | null>(null);
  const [votes, setVotes] = useState<Record<string, string>>({});

  if (!poll) {
    return null;
  }

  const chosen = votes[poll.id] ?? rememberedVote(poll.id);

  const vote = async (optionId: string) => {
    setVoting(optionId);
    try {
      await voteInPoll(poll.id, optionId);
      rememberVote(poll.id, optionId);
      setVotes((current) => ({ ...current, [poll.id]: optionId }));
      queryClient.setQueryData(livePollQuery().queryKey, (current) =>
        current
          ? {
              ...current,
              options: current.options.map((option) =>
                option.id === optionId
                  ? { ...option, responseCount: option.responseCount + 1 }
                  : option
              ),
              responseCount: current.responseCount + 1,
            }
          : current
      );
    } catch (error) {
      toast.error(apiErrorMessage(error));
    } finally {
      setVoting(null);
    }
  };

  return (
    <section
      aria-labelledby="live-poll-question"
      className="mx-auto w-full max-w-7xl px-6 pt-12 lg:px-10"
    >
      <div className="rounded-3xl border border-border/70 bg-background p-5 sm:p-6">
        <p className="flex items-center gap-2 text-xs font-semibold tracking-wide text-primary uppercase">
          <span
            aria-hidden
            className="size-2 animate-pulse rounded-full bg-primary"
          />
          Live poll
        </p>
        <h2
          className="mt-2 text-xl font-semibold tracking-tight text-balance"
          id="live-poll-question"
        >
          {poll.question}
        </h2>
        <div className="mt-5">
          {chosen ? (
            <PollResults chosenOptionId={chosen} poll={poll} />
          ) : (
            <div className="grid gap-2 sm:grid-cols-2">
              {poll.options.map((option) => (
                <button
                  className="min-h-11 rounded-xl border px-4 py-2.5 text-start font-medium transition-colors hover:border-primary hover:bg-primary/5 focus-visible:outline-2 focus-visible:outline-ring disabled:opacity-60"
                  disabled={voting !== null}
                  key={option.id}
                  onClick={() => vote(option.id)}
                  type="button"
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
